import CallEndIcon from "@mui/icons-material/CallEnd";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAccount, useToast } from "../../../context";
import {
  sendVoiceCallButton,
  startWebCall,
  terminateWebCall,
} from "../../../services/api";
import { ErrorHandler } from "../../../utils/error-handler";

type CallDialogProps = {
  open: boolean;
  onClose: () => void;
  companyId: string;
  to: string; // telefone do cliente (digits)
};

export const CallDialog: React.FC<CallDialogProps> = ({
  open,
  onClose,
  companyId,
  to,
}) => {
  const { socket } = useAccount();
  const { showError, showSuccess } = useToast();

  const [step, setStep] = useState<"idle" | "calling" | "connected" | "ended">(
    "idle",
  );
  const [callId, setCallId] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

  const socketId = socket.current?.id || "";

  const rtcConfig = useMemo(
    () => ({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    }),
    [],
  );

  const waitForIceGatheringComplete = (
    pc: RTCPeerConnection,
    timeoutMs = 5000,
  ) => {
    if (pc.iceGatheringState === "complete") return Promise.resolve();

    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        pc.removeEventListener("icegatheringstatechange", onStateChange);
        resolve();
      };
      const onStateChange = () => {
        if (pc.iceGatheringState === "complete") finish();
      };
      pc.addEventListener("icegatheringstatechange", onStateChange);
      setTimeout(finish, timeoutMs);
    });
  };

  useEffect(() => {
    if (!open) return;

    const s = socket.current;
    if (!s) return;

    const onConnect = (payload: any) => {
      if (!payload?.callId || payload.callId !== callId) return;
      const session = payload.session;
      if (!session?.sdp) return;

      const pc = pcRef.current;
      if (!pc) return;

      pc.setRemoteDescription({ type: "answer", sdp: session.sdp })
        .then(() => {
          setStep("connected");
        })
        .catch((e) => {
          console.error("Erro ao aplicar SDP answer:", e);
          showError("Erro ao conectar a chamada (SDP).");
          setStep("ended");
        });
    };

    const onCallEvent = (payload: any) => {
      if (!payload?.callId || payload.callId !== callId) return;
      if (payload?.call?.event === "terminate") {
        setStep("ended");
      }
    };

    s.on("call:connect", onConnect);
    s.on("call:event", onCallEvent);

    return () => {
      s.off("call:connect", onConnect);
      s.off("call:event", onCallEvent);
    };
  }, [open, socket, callId, showError]);

  const cleanup = useCallback(async () => {
    try {
      pcRef.current?.close();
    } catch (e) {
      const err = ErrorHandler.handleApiError(e);
      console.error("Erro ao fechar conexão:", err.userMessage);
    }
    pcRef.current = null;

    const ls = localStreamRef.current;
    if (ls) {
      ls.getTracks().forEach((t) => t.stop());
    }
    localStreamRef.current = null;

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setStep("idle");
      setCallId(null);
      cleanup();
    }
  }, [open, cleanup]);

  const handleRequestPermission = async () => {
    // MVP: enviar botão para o cliente ligar (gera call do lado do cliente e pode habilitar permissões/callbacks)
    try {
      await sendVoiceCallButton(companyId, to, {
        displayText: "Ligar agora",
        ttlMinutes: 60 * 24,
        bodyText:
          "Para habilitar ligações, toque no botão abaixo e nos ligue no WhatsApp.",
      });
      showSuccess("Enviamos ao cliente um botão para ele ligar no WhatsApp.");
    } catch (e) {
      const err = ErrorHandler.handleApiError(e);
      showError(
        err.userMessage || "Não foi possível solicitar permissão agora.",
      );
    }
  };

  const handleStart = async () => {
    try {
      if (!socketId) {
        showError(
          "Socket não conectado. Recarregue a página e tente novamente.",
        );
        return;
      }

      setStep("calling");

      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      localStreamRef.current = localStream;

      const pc = new RTCPeerConnection(rtcConfig);
      pcRef.current = pc;

      pc.ontrack = (event) => {
        const [stream] = event.streams;
        if (remoteAudioRef.current && stream) {
          remoteAudioRef.current.srcObject = stream;
        }
      };

      // Enviar áudio local
      localStream
        .getTracks()
        .forEach((track) => pc.addTrack(track, localStream));

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      // A Meta usa ICE-lite no answer; para aumentar a taxa de sucesso, envie o SDP já com candidates (quando possível)
      await waitForIceGatheringComplete(pc, 6000);
      const sdpOffer = pc.localDescription?.sdp || offer.sdp || "";
      const resp = await startWebCall({ companyId, to, sdpOffer, socketId });

      const newCallId = (resp as any)?.data?.data?.callId;
      if (!newCallId) {
        showError("Não foi possível iniciar a chamada.");
        setStep("ended");
        return;
      }

      setCallId(newCallId);
      showSuccess("Chamada iniciada. Aguardando o cliente atender...");
    } catch (error: any) {
      console.error("Erro ao iniciar chamada:", error?.response?.data || error);
      showError(error?.response?.data?.message || "Erro ao iniciar chamada.");
      setStep("ended");
      await cleanup();
    }
  };

  const handleHangup = async () => {
    try {
      if (callId) {
        await terminateWebCall({ companyId, callId });
      }
    } catch (e) {
      const err = ErrorHandler.handleApiError(e);
      console.error("Erro ao encerrar chamada:", err.userMessage);
      showError(err.userMessage || "Não foi possível encerrar a chamada.");
    }
    setStep("ended");
    await cleanup();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PhoneIcon fontSize="small" />
        Ligação (WhatsApp)
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "#667781" }}>
            Esta ligação acontece dentro do navegador (WebRTC). Você precisará
            permitir o microfone.
          </Typography>
          <Typography variant="body2" sx={{ color: "#667781" }}>
            Cliente: <b>{to}</b>
          </Typography>
          {callId && (
            <Typography variant="caption" sx={{ color: "#667781" }}>
              call_id: {callId}
            </Typography>
          )}
        </Box>

        <audio ref={remoteAudioRef} autoPlay>
          <track
            kind="captions"
            src="/captions-pt-BR.vtt"
            srcLang="pt-BR"
            label="Português"
          />
        </audio>
      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button onClick={handleRequestPermission} variant="outlined">
          Solicitar permissão
        </Button>
        {step === "idle" && (
          <Button onClick={handleStart} variant="contained">
            Iniciar chamada
          </Button>
        )}
        {step !== "idle" && (
          <Button
            onClick={handleHangup}
            color="error"
            variant="contained"
            startIcon={<CallEndIcon />}
          >
            Encerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
